import { type FC } from 'react';
import { MapPin, Navigation, ParkingCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import type { SpecialEvent } from '@/types';

interface LocationSectionProps {
  event: SpecialEvent;
}

/**
 * Location section displaying address, map, directions, and parking
 */
export const LocationSection: FC<LocationSectionProps> = ({ event }) => {
  const { locationDetails, directionsText, parkingInfo } = event;

  // Check if we have location details
  const hasLocationDetails =
    locationDetails &&
    (locationDetails.address || locationDetails.city || locationDetails.state);

  if (!hasLocationDetails && !directionsText && !parkingInfo) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-b from-turquoise-900 to-turquoise-800 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-turquoise-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-viking text-white mb-8 text-center drop-shadow-lg">Location</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Address Card */}
            {hasLocationDetails && (
              <Card className="bg-turquoise-800/50 backdrop-blur-sm border-2 border-turquoise-600/50 shadow-xl hover:border-orange-500/50 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                    <MapPin className="h-6 w-6 text-orange-400" />
                  </div>
                  <CardTitle className="text-xl font-viking text-orange-300">Address</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <p className="text-base font-medium text-white">{event.venue}</p>
                  {locationDetails.address && (
                    <p className="text-sm text-white/80">{locationDetails.address}</p>
                  )}
                  <p className="text-sm text-white/80">
                    {[locationDetails.city, locationDetails.state, locationDetails.zipCode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {locationDetails.mapUrl && (
                    <a
                      href={locationDetails.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors"
                    >
                      Open in Google Maps →
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Directions Card */}
            {directionsText && (
              <Card className="bg-turquoise-800/50 backdrop-blur-sm border-2 border-turquoise-600/50 shadow-xl hover:border-orange-500/50 transition-colors">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                    <Navigation className="h-6 w-6 text-orange-400" />
                  </div>
                  <CardTitle className="text-xl font-viking text-orange-300">Directions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {directionsText}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Parking Card */}
            {parkingInfo && (
              <Card className="bg-turquoise-800/50 backdrop-blur-sm border-2 border-turquoise-600/50 shadow-xl hover:border-orange-500/50 transition-colors lg:col-span-2">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center mb-3">
                    <ParkingCircle className="h-6 w-6 text-orange-400" />
                  </div>
                  <CardTitle className="text-xl font-viking text-orange-300">Parking Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-white/80 leading-relaxed">
                    {parkingInfo}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Google Maps Embed */}
          {locationDetails.mapUrl && locationDetails.mapUrl.includes('embed') && (
            <div className="mt-8 rounded-xl overflow-hidden shadow-2xl ring-2 ring-turquoise-600/50">
              <iframe
                src={locationDetails.mapUrl}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Event Location Map"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
